import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GroupSessionMemberModel,
  GroupSessionModel,
} from '../../core/api/models';
import { GroupSessionService } from '../../core/services/group-session.service';
import { OfflineDataService } from '../../core/services/offline-data.service';
import { AlertService } from '../../shared/services/alert.service';
import { PatientSessionService } from '../home/patient-session.service';
import { StaffHomeComponent } from '../home/staff-home.component';
import { AuthService } from '../../auth/auth.service';
import { AcademyModeService } from '../../core/services/academy-mode.service';
import { SessionCompletionResult } from '../home/session-completion-result';

@Component({
  selector: 'app-group-session',
  templateUrl: './group-session.component.html',
  styleUrl: './group-session.component.css',
  standalone: false,
})
export class GroupSessionComponent implements OnInit {
  @ViewChild(StaffHomeComponent) staffHome?: StaffHomeComponent;

  groupSession: GroupSessionModel | null = null;
  activeMember: GroupSessionMemberModel | null = null;

  get members(): GroupSessionMemberModel[] {
    return this.groupSession?.Members ?? [];
  }
  loading = true;
  starting = false;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private groupSessionService: GroupSessionService,
    private patientSessionService: PatientSessionService,
    private offlineDataService: OfflineDataService,
    private alertService: AlertService,
    private authService: AuthService,
    private academyModeService: AcademyModeService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const groupSessionID = Number(params['groupSessionID']);
        if (!Number.isFinite(groupSessionID) || groupSessionID === 0) {
          this.loading = false;
          this.alertService.error('Invalid group session');
          return;
        }
        this.loadGroup(groupSessionID);
      });
  }

  selectMember(member: GroupSessionMemberModel): void {
    this.refreshMember(member);
    this.activeMember = member;
  }

  async startAll(): Promise<void> {
    if (!this.groupSession || this.starting) {
      return;
    }
    this.starting = true;
    try {
      await this.patientSessionService.startGroupSessions(
        this.members.flatMap((member) =>
          member.PatientID != null && member.SessionID != null
            ? [{ PatientID: member.PatientID, SessionID: member.SessionID }]
            : []
        )
      );
      this.refreshMembers();
    } catch (error: any) {
      this.alertService.error(
        error?.message || 'Failed to start the group session'
      );
    } finally {
      this.starting = false;
    }
  }

  endActive(): void {
    this.staffHome?.endSession();
  }

  onActiveSessionFinished(result: SessionCompletionResult): void {
    if (!result.success || !this.groupSession || !this.activeMember) {
      return;
    }
    this.refreshMembers();
    const nextMember = this.members.find(
      (member) => !member.FinishedDate && !member.DeletedDate
    );
    if (nextMember) {
      this.selectMember(nextMember);
    } else {
      const patientID = this.activeMember.PatientID;
      if (result.offline) {
        if (patientID != null) this.navigateAfterGroupCompletion(patientID);
        return;
      }
      this.alertService.success('All group sessions have ended successfully');
      this.openGroupDocumentation(() => {
        if (patientID != null) {
          this.navigateAfterGroupCompletion(patientID);
        }
      });
    }
  }

  private navigateAfterGroupCompletion(patientID: number): void {
    if (this.offlineDataService.isInOfflineMode()) {
      this.router.navigate(['/offline/patients']);
      return;
    }
    this.router.navigate(['/staff', patientID, 'sessions'], {
      queryParams: this.academyModeService.getAcademyModeQueryParams(),
    });
  }

  private openGroupDocumentation(onComplete?: () => void): void {
    const serviceID = this.groupSession?.ServiceID;
    const groupSessionID = this.groupSession?.ID;
    if (
      !serviceID ||
      !groupSessionID ||
      this.offlineDataService.isInOfflineMode() ||
      !this.groupSession
    ) {
      onComplete?.();
      return;
    }
    const hasPendingMember = this.members.some(member => {
      if (member.DeletedDate) return false;
      const session = member.PatientID == null ? undefined : this.offlineDataService
        .getOfflinePatientData(member.PatientID)?.sessions.find(item => item.ID === member.SessionID);
      return !session || !session.FinishedDate || !!session.isDirty ||
        this.offlineDataService.hasUnsyncedPatientData(member.PatientID!);
    });
    if (hasPendingMember) {
      onComplete?.();
      return;
    }
    const promptKey = `sparkGroupDocumentationPrompted_${groupSessionID}`;
    if (localStorage.getItem(promptKey)) {
      onComplete?.();
      return;
    }
    localStorage.setItem(promptKey, '1');

    this.alertService
      .confirm(
        'Group session ended successfully',
        'Would you like to open the OptoMiser documentation for every client?'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) {
          onComplete?.();
          return;
        }
        const optoURL = this.authService.getOptoURL();
        for (const member of this.members) {
          const path =
            '/TreatmentReports/Documentation/CreateSparkDoc/' +
            serviceID +
            '/' +
            member.PatientID;
          window.open(optoURL ? optoURL + path : path);
        }
        onComplete?.();
      });
  }

  hasStartedSessions(): boolean {
    return (
      this.members.some(
        (member) =>
          !!member.StartedDate &&
          !member.FinishedDate &&
          !member.DeletedDate
      )
    );
  }

  hasUnstartedSessions(): boolean {
    return (
      this.members.some(
        (member) =>
          !member.StartedDate &&
          !member.FinishedDate &&
          !member.DeletedDate
      )
    );
  }

  private loadGroup(groupSessionID: number): void {
    this.loading = true;
    this.groupSessionService
      .getGroupSession(groupSessionID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (group) => {
          this.groupSession = group;
          this.refreshMembers();
          this.activeMember =
            this.members.find(
              (member) => !member.FinishedDate && !member.DeletedDate
            ) ??
            this.members.find((member) => !member.DeletedDate) ??
            this.members[0] ??
            null;
          this.loading = false;
          if (
            this.members.length > 0 &&
            this.members.every(
              (member) => !!member.FinishedDate || !!member.DeletedDate
            )
          ) {
            this.openGroupDocumentation();
          }
        },
        error: (error) => {
          this.loading = false;
          this.alertService.error(
            error?.error?.Message || error?.message || 'Failed to load group session'
          );
        },
      });
  }

  private refreshMembers(): void {
    this.members.forEach((member) => this.refreshMember(member));
  }

  private refreshMember(member: GroupSessionMemberModel): void {
    if (member.PatientID == null) {
      return;
    }
    const session = this.offlineDataService
      .getOfflinePatientData(member.PatientID)
      ?.sessions.find((item) => item.ID === member.SessionID);
    if (!session) {
      return;
    }
    member.StartedDate = session.StartedDate;
    member.FinishedDate = session.FinishedDate;
    member.DeletedDate = session.DeletedDate;
  }
}
